import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

// Avisos efímeros de la app. NADA de `alert()` (policies/ux-ui-design.md §2).
//
// Un toast que sólo aparece no existe para quien usa lector de pantalla: la región va con
// `aria-live` (§7.2 regla 7) — `polite` para lo bueno, `assertive` para lo que salió mal.
//
// LÍMITE DE DOMINIO: esto NO es una bandeja de notificaciones. Nexa no construye una
// (Regla de Oro 6): los avisos a personas viajan por la capa del ecosistema. Acá sólo vive el
// feedback inmediato de una acción que la persona acaba de hacer.

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  tone: ToastTone;
  title: string;
  detail?: string;
}

interface ToastContextValue {
  show: (toast: Omit<ToastMessage, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_TTL_MS = 6_000;

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-emerald-200/70 bg-emerald-50/90 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/70 dark:text-emerald-100',
  error: 'border-rose-200/70 bg-rose-50/90 text-rose-900 dark:border-rose-900/40 dark:bg-rose-950/70 dark:text-rose-100',
  info: 'border-zinc-200/70 bg-white/90 text-zinc-900 dark:border-zinc-700/60 dark:bg-zinc-900/90 dark:text-zinc-100',
};

/** Prefijo textual del tono: el color no puede ser el único portador del significado. */
const TONE_LABEL: Record<ToastTone, string> = {
  success: 'Listo',
  error: 'Error',
  info: 'Aviso',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const show = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { ...toast, id }]);
    timers.current.set(id, setTimeout(() => dismiss(id), DEFAULT_TTL_MS));
  }, [dismiss]);

  // Los timers se limpian al desmontar: si no, un test o una navegación dejan callbacks vivos
  // apuntando a estado muerto.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast necesita estar dentro de <ToastProvider>');
  return ctx;
}

export function ToastViewport({ toasts, onDismiss }: { toasts: ToastMessage[]; onDismiss: (id: string) => void }) {
  if (typeof document === 'undefined') return null;

  // DOS regiones vivas separadas y siempre presentes en el DOM: un `aria-live` que aparece junto
  // con su contenido muchas veces no se anuncia. Los errores van por `assertive` porque
  // interrumpen a propósito.
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-3 p-4 sm:items-end sm:p-6">
      <div role="status" aria-live="polite" className="contents">
        {toasts.filter((t) => t.tone !== 'error').map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
      <div role="alert" aria-live="assertive" className="contents">
        {toasts.filter((t) => t.tone === 'error').map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
    </div>,
    document.body,
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }) {
  return (
    <div
      className={[
        'pointer-events-auto w-full max-w-sm rounded-2xl border p-6 shadow-xl backdrop-blur-xl',
        'animate-in fade-in slide-in-from-bottom-4 duration-500',
        TONE_STYLES[toast.tone],
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            <span className="sr-only">{TONE_LABEL[toast.tone]}: </span>
            {toast.title}
          </p>
          {toast.detail && <p className="mt-1 text-sm leading-relaxed opacity-90">{toast.detail}</p>}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          aria-label="Descartar aviso"
          className="-mr-1 -mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                     transition-colors duration-300 hover:bg-black/5
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current dark:hover:bg-white/10"
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
